import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, ChevronDown, Wand2 } from 'lucide-react'
import type { CompanySetupStatus } from '@/types/setup-wizard.types'
import { Button } from '@/components/ui'
import { cn } from '@/lib/cn'

export interface SetupChecklistStripProps {
  companyId: string
  status: CompanySetupStatus | null
  isLoading: boolean
}

/**
 * Setup progress as a meter plus the actual checklist.
 *
 * The API already returns every item and its completed flag; showing only
 * "3 of 5 complete" made the operator open the wizard just to find out which
 * two were missing.
 */
export function SetupChecklistStrip({
  companyId,
  status,
  isLoading,
}: SetupChecklistStripProps): React.ReactElement | null {
  const [expanded, setExpanded] = useState(false)
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <div className="mt-4 h-14 animate-pulse rounded-[var(--radius-panel)] border border-gray-200 bg-white dark:border-gray-700/80 dark:bg-gray-800" />
    )
  }

  if (!status) return null

  const { completed, total, percent, state } = status.progress
  const isReady = state === 'ready'
  const remaining = status.checklist.filter((i) => !i.completed)

  return (
    <div className="mt-4 rounded-[var(--radius-panel)] border border-gray-200 bg-white shadow-[var(--shadow-panel)] dark:border-gray-700/80 dark:bg-gray-800 dark:shadow-none">
      <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
              isReady
                ? 'bg-teal-50 text-teal-700 dark:bg-teal-400/10 dark:text-teal-400'
                : 'bg-amber-50 text-amber-800 dark:bg-amber-400/10 dark:text-amber-300'
            )}
            aria-hidden
          >
            {isReady ? <Check className="h-3.5 w-3.5" /> : <Wand2 className="h-3.5 w-3.5" />}
          </span>

          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {isReady ? 'Setup complete' : `Setup ${completed} of ${total} complete`}
            </p>
            <div
              className="mt-1.5 h-1 w-full max-w-xs overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700"
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Company setup progress"
            >
              <span
                className={cn(
                  'block h-full rounded-full transition-[width] duration-500 ease-out',
                  isReady ? 'bg-teal-600 dark:bg-teal-500' : 'bg-amber-500 dark:bg-amber-400'
                )}
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {status.checklist.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls="setup-checklist-items"
            >
              {expanded ? 'Hide' : isReady ? 'View checks' : `Show ${remaining.length} remaining`}
              <ChevronDown
                className={cn(
                  'ml-1.5 h-3.5 w-3.5 transition-transform duration-200',
                  expanded && 'rotate-180'
                )}
                aria-hidden
              />
            </Button>
          )}
          {!isReady && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/companies/${companyId}/setup-wizard`)}
            >
              Continue setup
            </Button>
          )}
        </div>
      </div>

      {expanded && status.checklist.length > 0 && (
        <ul
          id="setup-checklist-items"
          className="grid gap-x-6 gap-y-2 border-t border-gray-100 px-4 py-3 sm:grid-cols-2 dark:border-gray-700/70"
        >
          {status.checklist.map((item) => (
            <li key={item.key} className="flex items-center gap-2 text-sm">
              <span
                className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                  item.completed
                    ? 'border-teal-600 bg-teal-600 text-white dark:border-teal-500 dark:bg-teal-500'
                    : 'border-gray-300 dark:border-gray-600'
                )}
                aria-hidden
              >
                {item.completed && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
              </span>
              <span
                className={cn(
                  item.completed
                    ? 'text-gray-500 dark:text-gray-400'
                    : 'font-medium text-gray-900 dark:text-gray-100'
                )}
              >
                {item.label}
              </span>
              <span className="sr-only">{item.completed ? '(complete)' : '(not complete)'}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

SetupChecklistStrip.displayName = 'SetupChecklistStrip'
